import { createContext, useContext, useEffect, useState } from "react";
import { getSettings as fetchSettings } from "../services/settingsService";
import { useTheme } from "./ThemeContext";

/**
 * Loads the workspace settings once and exposes them app-wide so the shell
 * (org name, timezone) and theme (brand accent) reflect the SAVED values —
 * the server is the source of truth, not just localStorage / hardcoded text.
 *
 * Applying a loaded settings row:
 *   - brand_accent  -> re-tints the app via ThemeContext.setAccent
 *   - organisation_name -> browser tab title
 */
const HEX = /^#[0-9a-fA-F]{6}$/;

// Module-level cache so route changes (which remount this provider) reuse the
// already-loaded settings instead of refetching and re-flickering the theme.
let cachedSettings = null;

const SettingsContext = createContext({
  settings: null,
  loading: true,
  refresh: async () => {},
});

export function SettingsProvider({ children }) {
  const { setAccent } = useTheme();
  const [settings, setSettings] = useState(cachedSettings);
  const [loading, setLoading] = useState(!cachedSettings);

  const apply = (s) => {
    if (!s) return;
    cachedSettings = s;
    setSettings(s);
    if (HEX.test(s.brand_accent || "")) setAccent(s.brand_accent); // server wins
    if (s.organisation_name) document.title = `${s.organisation_name} · Attendance`;
  };

  const refresh = async () => {
    const s = await fetchSettings();
    if (s) apply(s);
    return s;
  };

  useEffect(() => {
    if (cachedSettings) {
      apply(cachedSettings);
      return;
    }
    (async () => {
      await refresh();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, loading, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}

export default SettingsContext;
